import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../../components/layout/Layout";
import HierarchyChart from "../../../components/ui/HierarchyChart";
import SafeDeleteDialog from "../../../components/ui/SafeDeleteDialog";
import {
  useCompanyTree,
  useDeleteCompany,
  useSaveCompany,
} from "../../../api/queries/organizationQueries";
import usePermissions from "../../../hooks/usePermissions";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../../utils/apiError";
import { CompanyFormDialog, subtreeIds } from "./CompanyDialogs";
import CompanyDrawer from "./CompanyDrawer";
import CompanyTable from "./CompanyTable";
import "../admin.css";

const EMPTY = [];

/** Tree → flat [{ id, companyName, depth }] (parent picker). */
const flatten = (nodes, depth = 0) =>
  nodes.flatMap((n) => [
    { id: n.id, companyName: n.companyName, depth },
    ...flatten(n.children || [], depth + 1),
  ]);
const findNode = (nodes, id) => {
  for (const n of nodes) {
    if (n.id === id) return n;
    const hit = findNode(n.children || [], id);
    if (hit) return hit;
  }
  return null;
};

/**
 * Administration → Companies: the company tree (company-setup, COMPANY scope).
 * Create / edit / move / delete need modify; a top-level company only the super admin.
 * Click a company for its details, addresses and branches.
 */
const CompaniesPage = () => {
  const user = useCurrentUser();
  const { companyWide, superAdmin } = usePermissions();
  const canModify = companyWide("company-setup", "modify");
  const query = useCompanyTree();
  const nodes =
    query.data?.data || (Array.isArray(query.data) ? query.data : EMPTY);
  const options = useMemo(() => flatten(nodes), [nodes]);
  const save = useSaveCompany();
  const remove = useDeleteCompany();

  const [form, setForm] = useState(null); // { company?, parentId? }
  const [openId, setOpenId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const open = openId ? findNode(nodes, openId) : null;

  // Row actions (same permissions as before); inner buttons don't open the drawer
  const actions = (n) =>
    canModify && (
      <span
        className="d-inline-flex gap-1"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="btn btn-sm btn-light border"
          title="Add sub-company"
          aria-label={`Add sub-company to ${n.companyName}`}
          onClick={() => setForm({ parentId: n.id })}
        >
          <i className="bi bi-plus-lg"></i>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-light border"
          title="Edit"
          aria-label={`Edit ${n.companyName}`}
          onClick={() => setForm({ company: { ...n, subtree: subtreeIds(n) } })}
        >
          <i className="bi bi-pencil"></i>
        </button>
        {n.id !== user.companyId && (n.parentId || superAdmin) && (
          <button
            type="button"
            className="btn btn-sm btn-light border text-danger"
            title="Delete"
            aria-label={`Delete ${n.companyName}`}
            onClick={() => setDeleting(n)}
          >
            <i className="bi bi-trash"></i>
          </button>
        )}
      </span>
    );
  const card = (n) => (
    <>
      <div className="hc-card-title">{n.companyName}</div>
      <div className="hc-card-badges">
        {n.type && (
          <span
            className={`status-pill status-pill--${/division/i.test(n.type) ? "blue" : "green"}`}
          >
            {n.type}
          </span>
        )}
        {n.isActive === false && (
          <span className="status-pill status-pill--gray">Inactive</span>
        )}
        {n.id === user.companyId && (
          <span className="chip chip--sm">Your company</span>
        )}
      </div>
      <div className="hc-card-sub mt-1">
        {n.branchCount ?? 0} branch{n.branchCount === 1 ? "" : "es"} ·{" "}
        {n.employeeCount ?? 0} employees
        {(n.totalEmployeeCount ?? 0) !== (n.employeeCount ?? 0)
          ? ` (total ${n.totalEmployeeCount ?? 0})`
          : ""}
      </div>
      {(n.region || n.city) && (
        <div className="hc-card-sub">
          {[n.city, n.region].filter(Boolean).join(" · ")}
        </div>
      )}
      {canModify && <div className="hc-card-actions">{actions(n)}</div>}
    </>
  );
  const row = (n) => (
    <>
      <span className="org-name">{n.companyName}</span>
      {n.type && <span className="org-meta">{n.type}</span>}
      {n.isActive === false && (
        <span className="status-pill status-pill--gray">Inactive</span>
      )}
      {n.id === user.companyId && (
        <span className="chip chip--sm">Your company</span>
      )}
      <span className="org-meta ms-auto">
        <i className="bi bi-diagram-2 me-1"></i>
        {n.branchCount ?? 0}
      </span>
      <span className="org-meta">
        <i className="bi bi-people me-1"></i>
        {n.employeeCount ?? 0} / {n.totalEmployeeCount ?? 0}
      </span>
      <button
        type="button"
        className="btn btn-sm btn-link p-0"
        onClick={() => setOpenId(n.id)}
      >
        Details
      </button>
      {actions(n)}
    </>
  );

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">Companies</h1>
            <p className="text-muted small mb-0">
              Your company and its subsidiaries, as a chart or a table.
              Headcount: own / including sub-companies.
            </p>
          </div>
          {superAdmin && (
            <button
              type="button"
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={() => setForm({ parentId: "" })}
            >
              <i className="bi bi-plus-lg me-2"></i> New top-level company
            </button>
          )}
        </div>

        <div className="admin-card">
          {query.isLoading && (
            <div className="text-center py-5">
              <span
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              ></span>
            </div>
          )}
          {query.isError && (
            <div className="alert alert-danger fs-085 m-0">
              {getApiErrorMessage(query.error, "Could not load the companies.")}
            </div>
          )}
          {!query.isLoading && !query.isError && nodes.length === 0 && (
            <p className="fs-085 text-slate-500 m-0">No companies.</p>
          )}
          {nodes.length > 0 && (
            <HierarchyChart
              label="Companies"
              nodes={nodes}
              renderCard={card}
              renderRow={row}
              isInactive={(n) => n.isActive === false}
              searchText={(n) =>
                `${n.companyName} ${n.type || ""} ${n.region || ""} ${n.city || ""}`
              }
              onOpen={(n) => setOpenId(n.id)}
              renderTable={(q) => (
                <CompanyTable
                  nodes={nodes}
                  query={q}
                  actions={actions}
                  onOpen={(n) => setOpenId(n.id)}
                  yourCompanyId={user.companyId}
                />
              )}
            />
          )}
        </div>
      </div>

      {form && (
        <CompanyFormDialog
          company={form.company}
          parentId={form.parentId}
          options={options}
          superAdmin={superAdmin}
          onClose={() => setForm(null)}
        />
      )}
      {open && (
        <CompanyDrawer
          company={open}
          canModify={canModify}
          onClose={() => setOpenId(null)}
        />
      )}
      {deleting && (
        <SafeDeleteDialog
          title="Delete company?"
          name={deleting.companyName}
          isActive={deleting.isActive !== false}
          onDelete={() => remove.mutateAsync(deleting.id)}
          onDeactivate={() =>
            save.mutateAsync({ id: deleting.id, isActive: false })
          }
          onClose={() => setDeleting(null)}
        />
      )}
    </Layout>
  );
};

export default CompaniesPage;
